import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

// PDF.js worker вже налаштований в fileTextExtractor.js

/**
 * Конвертує сторінку PDF в зображення (canvas → base64)
 * @param {PDFPageProxy} page - Сторінка PDF
 * @param {number} scale - Масштаб для кращої якості OCR
 * @returns {Promise<HTMLCanvasElement>} - Canvas з зображенням
 */
const convertPageToCanvas = async (page, scale = 2) => {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;

  return canvas;
};

/**
 * Витягує текст з PDF файлу використовуючи Tesseract.js OCR
 * @param {File} file - PDF файл
 * @param {number} maxPages - Максимальна кількість сторінок для OCR
 * @returns {Promise<string|null>} - Розпізнаний текст або null
 */
export const extractTextWithTesseractOCR = async (file, maxPages = 3) => {
  let worker = null;

  try {
    console.log(`Starting Tesseract.js OCR for ${file.name} (max ${maxPages} pages)...`);
    const startTime = performance.now();

    // Крок 1: Завантажуємо PDF
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    const pagesToProcess = Math.min(pdf.numPages, maxPages);
    console.log(`PDF has ${pdf.numPages} pages, processing ${pagesToProcess} pages`);

    // Крок 2: Створюємо Tesseract worker
    console.log('Creating Tesseract.js worker...');
    worker = await createWorker('eng+ukr+rus', 1, {
      // Завантажуємо моделі з CDN
      workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/worker.min.js',
      langPath: 'https://tessdata.projectnaptha.com/4.0.0',
      corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@5/tesseract-core-simd.wasm.js',
      logger: (m) => {
        // Логуємо тільки важливі повідомлення
        if (m.status === 'recognizing text') {
          console.log(`OCR progress: ${Math.round(m.progress * 100)}%`);
        }
      }
    });

    console.log('Tesseract.js worker created');

    // Крок 3: OCR для кожної сторінки
    const allPageTexts = [];

    for (let pageNum = 1; pageNum <= pagesToProcess; pageNum++) {
      console.log(`Processing page ${pageNum}/${pagesToProcess}...`);

      const page = await pdf.getPage(pageNum);
      const canvas = await convertPageToCanvas(page, 2); // Scale 2 для кращої якості

      // Розпізнаємо текст з canvas
      const { data } = await worker.recognize(canvas);

      const pageText = data.text.trim();
      console.log(`Page ${pageNum} OCR: ${pageText.length} characters`);

      if (pageText.length > 0) {
        allPageTexts.push(pageText);
      }
    }

    // Крок 4: Закриваємо worker
    await worker.terminate();
    worker = null;

    const endTime = performance.now();
    const duration = ((endTime - startTime) / 1000).toFixed(1);

    // Об'єднуємо текст з усіх сторінок
    const extractedText = allPageTexts.join('\n\n');
    const textLength = extractedText.length;

    console.log(`Tesseract.js OCR completed in ${duration}s`);
    console.log(`Total extracted: ${textLength} characters`);

    if (textLength > 0) {
      console.log('First 200 chars:', extractedText.substring(0, 200));
      return extractedText;
    }

    console.warn('Tesseract.js OCR returned empty text');
    return null;

  } catch (error) {
    console.error('Tesseract.js OCR error:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });

    // Закриваємо worker у випадку помилки
    if (worker) {
      try {
        await worker.terminate();
      } catch (terminateError) {
        console.error('Error terminating worker:', terminateError);
      }
    }

    // Повертаємо null замість викидання помилки
    return null;
  }
};

/**
 * Перевіряє підтримку Tesseract.js (завжди true в браузері)
 * @returns {boolean}
 */
export const isTesseractSupported = () => {
  return typeof window !== 'undefined';
};
